import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CHORE_THEMES, fmtMoney } from "@/lib/themes";
import { ChoreWheel } from "@/components/ChoreWheel";
import { ParticleLayer } from "@/components/ParticleLayer";
import { toast } from "sonner";

export function WheelTab({ onData }) {
  const { activeMember, refreshMembers } = useAuth();
  const [chores, setChores] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [spinning, setSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [celebrate, setCelebrate] = useState(null); // theme emojis
  const [result, setResult] = useState(null);

  const load = async () => {
    const [c, a] = await Promise.all([api.get("/chores"), api.get("/assignments")]);
    setChores(c.data);
    setAssignments(a.data);
  };

  useEffect(() => {
    load();
  }, []);

  const myAssignments = useMemo(
    () => assignments.filter((a) => a.member_id === activeMember?.id),
    [assignments, activeMember]
  );

  const spin = async () => {
    if (!activeMember) return toast.error("Pick who you are first!");
    if (chores.length === 0) return toast.error("Add some chores first (Manage tab)");
    setSpinning(true);
    setResult(null);
    setCelebrate(null);
    // visual spin
    const turns = 6 + Math.random() * 3;
    setRotation((r) => r + turns * 360);
    try {
      const { data } = await api.post("/spin", { member_id: activeMember.id, count: 5 });
      setTimeout(async () => {
        setSpinning(false);
        setAssignments(data);
        const mine = data.filter((a) => a.member_id === activeMember.id);
        setResult(mine);
        // celebration particles from first chore theme
        const themes = mine.map((a) => a.chore?.theme).filter(Boolean);
        const emojis = themes.flatMap((t) => (CHORE_THEMES[t] || {}).emojis || []);
        setCelebrate(emojis.length ? emojis : ["🎉", "⭐", "✨"]);
        toast.success(`${activeMember.name} got ${mine.length} chores this week!`);
        onData?.();
        setTimeout(() => setCelebrate(null), 9000);
      }, 4100);
    } catch (e) {
      setSpinning(false);
      toast.error("Could not spin. Try again.");
    }
  };

  return (
    <div className="space-y-8">
      {celebrate && <ParticleLayer emojis={celebrate} active count={26} />}

      <div className="grid lg:grid-cols-2 gap-8 items-start">
        <div className="card-tactile p-6 sm:p-8">
          <h2 className="text-2xl font-extrabold mb-1" style={{ fontFamily: "Outfit" }}>
            Spin for your week
          </h2>
          <p className="text-slate-500 font-medium mb-6">
            {activeMember ? (
              <>Spinning as <b style={{ color: activeMember.color }}>{activeMember.name}</b> — you'll get up to 5 chores, no double-ups with siblings.</>
            ) : (
              "Pick who you are up top first."
            )}
          </p>
          <ChoreWheel
            chores={chores}
            spinning={spinning}
            rotation={rotation}
            onSpin={spin}
            disabled={!activeMember}
          />
        </div>

        <div className="card-tactile p-6 sm:p-8 min-h-[300px]">
          <h3 className="text-xl font-bold mb-4" style={{ fontFamily: "Outfit" }}>
            🎯 This week's chores {activeMember && `for ${activeMember.name}`}
          </h3>
          {(result || myAssignments).length === 0 ? (
            <div className="text-center py-12 text-slate-400 font-medium">
              <div className="text-5xl mb-3">🎡</div>
              No chores yet — give the wheel a spin!
            </div>
          ) : (
            <div className="space-y-3">
              {(result || myAssignments).map((a) => {
                const theme = CHORE_THEMES[a.chore?.theme] || CHORE_THEMES.washing_up;
                return (
                  <div
                    key={a.id}
                    data-testid="assigned-chore-card"
                    className="flex items-center gap-3 p-3 rounded-xl border-2"
                    style={{ background: theme.bg, borderColor: theme.color + "55" }}
                  >
                    <span className="text-2xl">{theme.emojis[0]}</span>
                    <div className="flex-1">
                      <div className="font-bold">{a.chore?.title}</div>
                      <div className="text-xs font-semibold text-slate-500">{theme.label}</div>
                    </div>
                    <div className="font-extrabold" style={{ color: "#10B981" }}>
                      {fmtMoney(a.chore?.reward)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <p className="text-xs text-slate-400 mt-4 font-medium">
            Head to <b>My Chores</b> to tick them off and earn rewards.
          </p>
        </div>
      </div>
    </div>
  );
}
